import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { invoke, convertFileSrc } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoViewer } from "./PhotoViewer";
import {
  store, todayLocal, prettyDate, spanCopy, displayPlace, normalizePlace, DEFAULT_PLACES,
  buildWeightTrend, CARE_KINDS, loadActivePetId, saveActivePetId,
  type Pet, type Moment, type Overall, type RemembranceSignals, type HeadlineFact, type FactTone, type FocusInput,
  type Care, type CareKind,
} from "./store";
import { weaveRemembrance, openWebLogin, BrainError, type BrainErrorCode } from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import { buildVetSummary, vetSummaryFilename } from "./vet-summary";
import { syncReminders, type Reminder } from "./reminders";
import { loadRecap, saveRecap, recapReminders, RecapControl, type RecapSetting } from "./recap";
import Chat from "./chat/Chat";
import portraitPlaceholder from "./assets/portrait-placeholder.svg";
import emptyNoPet from "./assets/empty-no-pet.svg";
import emptyFirstMoment from "./assets/empty-first-moment.svg";
import {
  Sparkles, Trash2, X, Lock, PawPrint, WifiOff, RefreshCw, Star, Pencil, MapPin, ImageIcon,
  Heart, LifeBuoy, Check, Copy, CalendarHeart, CalendarDays, Quote, Plus, Stethoscope, Scale, ChevronDown, Bell, Share2,
} from "lucide-react";

const LIST_CAP = 50000;

/**
 * the animal itself — the one mark this app can own outright.
 * Replaces the lucide `Sparkles` that 20 of the fleet's 26 chat launchers rendered in common;
 * the button outline changes with it, because one glyph in two colours is still one control
 * (Guideline 4.3(a)).
 */
function PawMark({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <ellipse cx="12" cy="15.6" rx="5.1" ry="4.2" stroke="currentColor" strokeWidth="1.6" />
      <ellipse cx="6.6" cy="9.9" rx="1.9" ry="2.4" fill="currentColor" />
      <ellipse cx="10.6" cy="7.4" rx="1.9" ry="2.5" fill="currentColor" />
      <ellipse cx="15" cy="7.7" rx="1.9" ry="2.5" fill="currentColor" />
      <ellipse cx="18.2" cy="10.6" rx="1.8" ry="2.3" fill="currentColor" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-forever-paw://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window navigated to oriora-forever-paw://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Forever Paw"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootSplash />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots (ABOVE the gates, per the
// pipeline doc's P1 trap). ⚠️ MUST stay 0 in committed/shipped code (per-app ledger checks this).
const SCREENSHOT = 0;

function BootSplash() {
  return (
    <div style={{ height: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <div className="empty-art" style={{ width: 64, height: 64, borderRadius: 18 }}><PawPrint size={30} /></div>
        <div className="skeleton-line" style={{ width: 120, height: 10 }} />
      </div>
    </div>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><PawPrint size={34} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}>Forever Paw</div>
        <h1 className="serif" style={{ fontSize: "1.55rem", margin: "0.5rem 0 0.5rem", letterSpacing: "-0.02em" }}>A life worth keeping</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Keep their moments, their weight and their vet visits in one place. Everything stays on your device unless you
          back it up — and Oriora gently weaves it into their story when you ask, through your own account.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-forever-paw");
              else if (p === "macos") await signInMac("oriora-forever-paw");
              else if (p === "web") window.location.href = "/login?next=/oriora-forever-paw/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "1rem", lineHeight: 1.5 }}>
          Opens Oriora to sign in, then returns here.
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [pet, setPet] = useState<Pet | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [activePetId, setActivePetId] = useState<string | null>(() => loadActivePetId());
  const [showPetPicker, setShowPetPicker] = useState(false);
  const [addingPet, setAddingPet] = useState(false);
  const [confirmDeletePet, setConfirmDeletePet] = useState<Pet | null>(null);
  const [moments, setMoments] = useState<Moment[]>([]);
  const [care, setCare] = useState<Care[]>([]);
  const [editingCare, setEditingCare] = useState<Care | null>(null);
  const [showCare, setShowCare] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState<Moment | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [showPet, setShowPet] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Moment | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not
  // change any existing screen. onChanged → reload() refreshes moments/pet after a chat mutation.
  const [chatOpen, setChatOpen] = useState(false);
  // Optional, off-by-default periodic "moment to remember" nudge (recap.tsx). Leads back to the
  // EXISTING "their story" card on a cadence the customer chooses; no new AI, no new call shape.
  const [recap, setRecap] = useState<RecapSetting>(loadRecap);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const rememberRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    // Resolve WHICH pet first — a stale saved id (its pet was deleted on another device, or the
    // backup restored a different book) must fall back to the first pet, never to an empty screen.
    const all = await store.listPets();
    const chosen = all.find((x) => x.id === activePetId) ?? all[0] ?? null;
    if (chosen && chosen.id !== activePetId) { setActivePetId(chosen.id); saveActivePetId(chosen.id); }
    const scope = chosen?.id ?? null;
    const [ms, cs] = await Promise.all([
      store.listMoments({ petId: scope, limit: LIST_CAP }),
      store.listCare(scope, undefined, LIST_CAP),
    ]);
    setPets(all);
    setPet(chosen);
    setMoments(ms);
    setCare(cs);
    setLoaded(true);
  }, [activePetId]);
  useEffect(() => { void reload(); }, [reload]);

  // Local reminders are derived from the care log's due dates and re-synced on every data change.
  // ⚠️ Scoped to ALL pets, not the active one: a booster due for the cat you are not currently
  // looking at must still fire. syncReminders replaces by key, so re-running is safe.
  useEffect(() => {
    if (!loaded) return;
    void (async () => {
      const everyPet = await store.listPets();
      const byId = new Map(everyPet.map((p) => [p.id, p.name?.trim() || "your companion"]));
      const rows = await store.listCare(null, undefined, LIST_CAP);
      const due: Reminder[] = rows
        .filter((c) => /^\d{4}-\d{2}-\d{2}$/.test(c.due_on || ""))
        .map((c) => {
          const who = byId.get(c.pet_id) ?? "your companion";
          const what = c.title?.trim() || CARE_KINDS.find((k) => k.kind === c.kind)?.label || "Care";
          // 9am local on the day — factual, never advice, matching the app's observational fence.
          return {
            key: c.id,
            title: `${what} — ${who}`,
            body: `Due today, from ${who}'s record.`,
            at: new Date(`${c.due_on}T09:00:00`),
          };
        });
      // Merge the optional recap nudge into the SAME call — syncReminders cancels + relays the
      // whole set, so the recap and the care-due reminders must be scheduled together.
      const recapName = pet?.name?.trim() || "your companion";
      await syncReminders([...due, ...recapReminders(recapName, recap)]);
    })();
  }, [loaded, care, pets, pet, recap]);

  // Native-menu actions (Actions menu in the macOS menu bar): Add / Remember / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setEditing(null); setShowLog(true); break;
        case "remember":
          rememberRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          rememberRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const overall = useMemo<Overall>(() => deriveOverall(moments), [moments]);

  const doExport = useCallback(() => {
    if (moments.length === 0) return;
    const { csv, filename } = buildExport(moments);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [moments]);
  exportRef.current = doExport;

  function startLog(m?: Moment) { setEditing(m ?? null); setShowLog(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteMoment(id);
    await reload();
  }

  const petName = pet?.name?.trim() || "your companion";

  if (!loaded) return <BootSplash />;

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span><strong>Offline.</strong> Their story is paused — your moments and their record still save on this device.</span>
        </div>
      )}

      <header className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
        <div className="row" style={{ gap: "0.55rem" }}>
          <div className="tile-icon" style={{ width: 34, height: 34, borderRadius: 10 }}><PawPrint size={18} /></div>
          <h1 className="serif" style={{ fontSize: "1.4rem", margin: 0, letterSpacing: "-0.02em" }}>Forever Paw</h1>
        </div>
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Forever Paw" fileSlug="forever-paw" appScheme="oriora-forever-paw"
            hasData={moments.length > 0} onRestored={reload}
            onExportCsv={moments.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug="oriora-forever-paw" appName="Oriora Forever Paw" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      <PetSwitcher
        pets={pets}
        active={pet}
        onPick={(id) => { setActivePetId(id); saveActivePetId(id); setShowPetPicker(false); }}
        open={showPetPicker}
        setOpen={setShowPetPicker}
        onAdd={() => { setShowPetPicker(false); setAddingPet(true); }}
      />

      <PetHero pet={pet} overall={overall} onEdit={() => setShowPet(true)} />

      {/* READY-STATE 2026-09-19 — before there is a named companion, one line does the whole
          first act: the name, what they are, and today's first dated entry. */}
      {!pet?.name?.trim() && care.length === 0 && <FirstRecord onStarted={reload} />}

      {/* READY-STATE: while the record door above is showing, this recedes to a secondary — two
          identical primary pills on one screen is no choice at all. Unchanged for anyone who
          already has a companion. */}
      <button className={`btn${!pet?.name?.trim() && care.length === 0 ? "" : " btn-primary"}`} style={{ width: "100%", marginTop: "0.85rem", borderRadius: 999, padding: "0.8rem 1.4rem", fontSize: "var(--fs-sm)", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.4rem" }} onClick={() => startLog()}>
        <Plus size={18} /> Add a moment
      </button>

      {moments.length > 0 && <MemoryInsights moments={moments} overall={overall} petId={pet?.id ?? null} />}

      <div ref={rememberRef}>
        <RemembranceCard moments={moments} petName={petName} petId={pet?.id ?? null} />
        <RecapControl setting={recap} onChange={(next) => { setRecap(next); saveRecap(next); }} />
      </div>

      <TheRecord
        care={care}
        pet={pet}
        petName={petName}
        onAdd={() => { setEditingCare(null); setShowCare(true); }}
        onEdit={(c) => { setEditingCare(c); setShowCare(true); }}
        onDelete={async (c) => { await store.deleteCare(c.id); await reload(); }}
      />

      <div className="sec-head">
        <h2><CalendarHeart size={17} style={{ color: "var(--accent)" }} /> Their moments</h2>
      </div>

      {moments.length === 0 ? (
        <div className="card fade-up empty-glow" style={{ padding: "2.75rem 1.75rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
          <img className="empty-illustration" src={emptyFirstMoment} alt="" aria-hidden="true" />
          <div className="serif" style={{ fontSize: "1.4rem", margin: "1rem 0 0.4rem", letterSpacing: "-0.02em" }}>Save your first moment</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginBottom: "1.5rem", lineHeight: 1.6, maxWidth: 320, marginInline: "auto" }}>
            A photo on the couch, the day you met, an ordinary morning. Every moment becomes part of their story.
          </div>
          <button className="btn btn-primary btn-pill" style={{ margin: "0 auto", minHeight: 46, padding: "0 1.5rem" }} onClick={() => startLog()}><Plus size={17} /> Add a moment</button>
        </div>
      ) : (
        <Timeline moments={moments} onEdit={startLog} onDelete={setConfirmDelete} />
      )}

      {showLog && (
        <MomentEditor editing={editing} petId={pet?.id ?? null} onClose={() => { setShowLog(false); setEditing(null); }} onSaved={() => { setShowLog(false); setEditing(null); void reload(); }} />
      )}

      {showPet && (
        <PetEditor
          pet={pet}
          onClose={() => setShowPet(false)}
          onSaved={() => { setShowPet(false); void reload(); }}
          onAddAnother={() => { setShowPet(false); setAddingPet(true); }}
          onDelete={pets.length > 1 ? () => { setShowPet(false); setConfirmDeletePet(pet); } : undefined}
        />
      )}

      {addingPet && (
        <PetEditor
          pet={null}
          isNew
          onClose={() => setAddingPet(false)}
          onSaved={(created) => {
            setAddingPet(false);
            if (created) { setActivePetId(created.id); saveActivePetId(created.id); }
            void reload();
          }}
        />
      )}

      {confirmDeletePet && (
        <CenterModal title={`Remove ${confirmDeletePet.name || "this companion"}?`} onClose={() => setConfirmDeletePet(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            This removes their whole book — every moment and everything in their record. It can&rsquo;t be undone.
            Your other companions are untouched.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDeletePet(null)}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ background: "var(--rose)", borderColor: "var(--rose)" }}
              onClick={async () => {
                const id = confirmDeletePet.id;
                setConfirmDeletePet(null);
                await store.deletePet(id);
                // Fall back to whichever pet remains, never to a dangling id.
                const rest = await store.listPets();
                const next = rest[0]?.id ?? null;
                setActivePetId(next); saveActivePetId(next);
                await reload();
              }}>Remove</button>
          </div>
        </CenterModal>
      )}

      {showCare && (
        <CareEditor
          editing={editingCare}
          petId={pet?.id ?? null}
          onClose={() => { setShowCare(false); setEditingCare(null); }}
          onSaved={() => { setShowCare(false); setEditingCare(null); void reload(); }}
        />
      )}

      

      {confirmDelete && (
        <CenterModal title="Delete this moment?" onClose={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            Remove “{confirmDelete.title || prettyDate(confirmDelete.happened_on)}” from the memory book? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary btn-pill" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </CenterModal>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads. */}
      {!chatOpen && (
        <button className="fpc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <PawMark size={19} />
        </button>
      )}
      <aside className={`fpc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="fpc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to save, find, or weave their story</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// Mirror of the headline numbers signals.rs computes — for the on-device hero (no AI).
function deriveOverall(moments: Moment[]): Overall {
  const dated = moments
    .map((m) => ({ d: m.happened_on.slice(0, 10), m }))
    .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x.d))
    .sort((a, b) => a.d.localeCompare(b.d));
  const total = dated.length;
  const milestone_count = dated.filter((x) => x.m.is_milestone).length;
  const with_photo = dated.filter((x) => !!x.m.photo).length;
  const first = dated[0]?.d ?? null;
  const last = dated[dated.length - 1]?.d ?? null;
  let span_days = 0, span_years = 0;
  if (first && last) {
    const f = new Date(first + "T00:00:00Z"); const l = new Date(last + "T00:00:00Z");
    span_days = Math.round((l.getTime() - f.getTime()) / 86400000) + 1;
    span_years = Math.max(0, l.getUTCFullYear() - f.getUTCFullYear() -
      ((l.getUTCMonth() < f.getUTCMonth() || (l.getUTCMonth() === f.getUTCMonth() && l.getUTCDate() < f.getUTCDate())) ? 1 : 0));
  }
  const distinct_days = new Set(dated.map((x) => x.d)).size;
  return {
    total_moments: total, milestone_count, ordinary_count: total - milestone_count,
    moments_with_photo: with_photo, span_years, span_days,
    first_moment_on: first, last_moment_on: last, distinct_days,
  };
}

/**
 * The pet switcher. Deliberately absent with a single pet: a household with one animal should see
 * exactly the app they saw in 1.2.0, with no chrome for a feature they do not use. It only appears
 * once a second companion exists — and the "add another" entry lives in the editor's own sheet so
 * a one-pet owner still has a way in.
 */
function PetSwitcher({ pets, active, onPick, open, setOpen, onAdd }: {
  pets: Pet[];
  active: Pet | null;
  onPick: (id: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  onAdd: () => void;
}) {
  if (pets.length <= 1) return null;
  return (
    <>
      <button
        className="btn btn-sm btn-pill"
        style={{ marginBottom: "0.6rem", gap: "0.35rem" }}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <PawPrint size={13} />
        {active?.name?.trim() || "Choose a companion"}
        <ChevronDown size={13} />
      </button>
      {open && (
        <BottomSheet title="Your companions" onClose={() => setOpen(false)}>
          <div style={{ display: "grid", gap: "0.4rem", marginBottom: "0.9rem" }}>
            {pets.map((p) => {
              const isActive = p.id === active?.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`chip${isActive ? " selected" : ""}`}
                  style={{ justifyContent: "flex-start", padding: "0.7rem 0.85rem", width: "100%" }}
                  onClick={() => onPick(p.id)}
                  aria-pressed={isActive}
                >
                  <span style={{ fontWeight: 600 }}>{p.name?.trim() || "Unnamed"}</span>
                  {p.kind ? <span className="muted" style={{ marginLeft: "0.4rem" }}>{displayPlace(p.kind)}</span> : null}
                  {p.farewell_on ? <span className="muted" style={{ marginLeft: "auto", fontSize: "var(--fs-xs)" }}>remembered</span> : null}
                </button>
              );
            })}
          </div>
          <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 44 }} onClick={onAdd}>
            <Plus size={15} /> Add another companion
          </button>
        </BottomSheet>
      )}
    </>
  );
}

function PetHero({ pet, overall, onEdit }: { pet: Pet | null; overall: Overall; onEdit: () => void }) {
  const name = pet?.name?.trim();
  const kind = pet?.kind?.trim();
  const remembered = !!pet?.farewell_on;
  const dateLine = (() => {
    const b = pet?.birth_on ? prettyDate(pet.birth_on) : "";
    const f = pet?.farewell_on ? prettyDate(pet.farewell_on) : "";
    if (b && f) return `${b} — ${f}`;
    if (b) return `Since ${b}`;
    return "";
  })();
  return (
    <div className="frontispiece fade-up" style={{ marginBottom: "1rem", textAlign: "center" }}>
      <button className="btn btn-on-hero btn-sm btn-pill frontispiece-edit" onClick={onEdit} aria-label="Edit companion details">
        <Pencil size={13} /> Edit
      </button>
      <div className="frontispiece-portrait" style={{ margin: "0 auto" }}>
        {pet?.photo ? (
          <img src={convertFileSrc(pet.photo)} alt={name ? `Portrait of ${name}` : "Portrait"} />
        ) : (
          <img src={portraitPlaceholder} alt="" aria-hidden="true" />
        )}
      </div>
      <div className="serif frontispiece-name">{name || "Name your companion"}</div>
      {dateLine && <div className="serif frontispiece-dates">{dateLine}{remembered ? " · forever remembered" : ""}</div>}
      <div className="frontispiece-prose" style={{ margin: "0.35rem auto 0" }}>
        {kind ? `${displayPlace(kind)} · ` : ""}{spanCopy(overall)}
        {overall.total_moments > 0 ? ` — ${overall.total_moments} ${overall.total_moments === 1 ? "moment" : "moments"} kept${overall.milestone_count > 0 ? `, ${overall.milestone_count} of them ${overall.milestone_count === 1 ? "a milestone" : "milestones"}` : ""}.` : ""}
      </div>
    </div>
  );
}

// ── Memory insights: the computed-figures dashboard (deterministic, no AI call) ──
function MemoryInsights({ moments, overall, petId }: { moments: Moment[]; overall: Overall; petId: string | null }) {
  const [signals, setSignals] = useState<RemembranceSignals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store.computeSignals(todayLocal(), undefined, petId)
      .then((s) => { if (alive) { setSignals(s); setLoading(false); } })
      .catch(() => { if (alive) { setSignals(null); setLoading(false); } });
    return () => { alive = false; };
  }, [moments.length, petId]);

  if (loading) {
    return (
      <div className="card" style={{ padding: "1rem", marginBottom: "1rem" }}>
        <div style={{ display: "grid", gap: "0.6rem" }}>
          <div className="skeleton-line" style={{ width: "88%" }} />
          <div className="skeleton-line" style={{ width: "72%" }} />
        </div>
      </div>
    );
  }
  if (!signals) return null;

  // R3 — the analytics grammar is gone. What survives, survives as WORDS.
  const places = signals.recurring_places.slice(0, 3).map((p) => displayPlace(p.place));
  const placeProse = places.length === 0 ? "" :
    places.length === 1 ? `${places[0]} — the place you kept coming back to.` :
    `${places.slice(0, -1).join(", ")} and ${places[places.length - 1]} — the places you kept coming back to.`;
  const spanProse = overall.span_years >= 1
    ? `${overall.span_years === 1 ? "A year" : `${spellSmall(overall.span_years)} years`} of moments, across ${overall.distinct_days} ${overall.distinct_days === 1 ? "day" : "days"} you chose to keep.`
    : overall.distinct_days > 1 ? `${overall.distinct_days} days you chose to keep.` : "";
  if (!spanProse && !placeProse) return null;
  return (
    <div className="card fade-up" style={{ padding: "1rem 1.2rem", marginBottom: "1.1rem" }}>
      {spanProse && <p className="recur-line serif" style={{ margin: 0, fontWeight: 500 }}>{spanProse}</p>}
      {placeProse && <p className="recur-line" style={{ margin: spanProse ? "0.45rem 0 0" : 0 }}><strong>{placeProse.split(" — ")[0]}</strong> — {placeProse.split(" — ")[1]}</p>}
    </div>
  );
}

/** Small numbers read better as words in prose. */
function spellSmall(n: number): string {
  const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
  return n >= 0 && n < words.length ? (n === 1 ? "One" : words[n][0].toUpperCase() + words[n].slice(1)) : String(n);
}

function StatCard({ label, value, unit, sub }: { label: React.ReactNode; value: string; unit?: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat-lbl">{label}</div>
      <div className="stat-val tnum">{value}{unit && <span className="unit">{unit}</span>}</div>
      {sub && <div className="stat-sub tnum">{sub}</div>}
    </div>
  );
}

function YearSparkline({ years }: { years: { year: number; count: number }[] }) {
  const recent = years.slice(-12);
  const max = Math.max(...recent.map((y) => y.count), 1);
  const peak = Math.max(...recent.map((y) => y.count));
  return (
    <div className="card" style={{ padding: "0.9rem 1rem", marginBottom: "0.7rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.7rem" }}>
        <div className="stat-lbl" style={{ margin: 0 }}>Moments per year</div>
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>{recent[0].year}–{recent[recent.length - 1].year}</div>
      </div>
      <div className="spark-bars" role="img" aria-label={`Moments saved across ${recent.length} years`}>
        {recent.map((y) => (
          <div key={y.year} className="spark-col" title={`${y.year} · ${y.count} ${y.count === 1 ? "moment" : "moments"}`}>
            <div className={`spark-bar${y.count === peak ? " peak" : ""}`} style={{ height: `${Math.max(8, (y.count / max) * 100)}%` }} />
            <span className="spark-cap tnum">{String(y.year).slice(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlacesBreakdown({ places }: { places: { place: string; count: number }[] }) {
  const top = places.slice(0, 5);
  const max = Math.max(...top.map((p) => p.count), 1);
  return (
    <div className="card" style={{ padding: "0.9rem 1rem" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.6rem" }}>The places you came back to</div>
      {top.map((p) => (
        <div key={p.place} className="spend-row">
          <span className="spend-name">{displayPlace(p.place)}</span>
          <div className="spend-track"><div className="spend-fill" style={{ width: `${Math.max(8, (p.count / max) * 100)}%` }} /></div>
          <span className="spend-val tnum">{p.count}</span>
        </div>
      ))}
    </div>
  );
}

// ── The ONE AI feature: weave a remembrance from HER saved moments ──────────────
// Comforting narration of saved memories only — never grief counseling / therapy /
// crisis advice. Each real-use path gets its own typed state. The safe deepening lever
// (AI-VALUE.md): re-weave around a chosen year / place / milestone — still ONE call.
type RememberErr = { code: BrainErrorCode | "empty"; message: string };
type FocusChoice = { input: FocusInput; label: string };

function RemembranceCard({ moments, petName, petId }: { moments: Moment[]; petName: string; petId: string | null }) {
  const [text, setText] = useState<string | null>(null);
  const [signals, setSignals] = useState<RemembranceSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<RememberErr | null>(null);
  const [focus, setFocus] = useState<FocusChoice | null>(null);

  // Re-weave focus choices, derived on-device from the saved moments (years + places +
  // milestones the owner can centre the remembrance on). "Whole book" is the default.
  const focusOptions = useMemo<FocusChoice[]>(() => {
    const dated = moments
      .map((m) => ({ d: m.happened_on.slice(0, 10), m }))
      .filter((x) => /^\d{4}-\d{2}-\d{2}$/.test(x.d));
    const years = Array.from(new Set(dated.map((x) => x.d.slice(0, 4)))).sort();
    const placeCounts = new Map<string, number>();
    for (const { m } of dated) if (m.place) placeCounts.set(m.place, (placeCounts.get(m.place) ?? 0) + 1);
    const places = Array.from(placeCounts.entries()).filter(([, c]) => c >= 2).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([p]) => p);
    const milestones = dated.filter((x) => x.m.is_milestone && x.m.title.trim()).map((x) => x.m.title.trim()).slice(0, 4);

    const opts: FocusChoice[] = [];
    if (years.length >= 2) for (const y of years) opts.push({ input: { kind: "year", value: y }, label: y });
    for (const p of places) opts.push({ input: { kind: "place", value: p }, label: displayPlace(p) });
    for (const t of milestones) opts.push({ input: { kind: "milestone", value: t }, label: t.length > 22 ? t.slice(0, 21) + "…" : t });
    return opts;
  }, [moments]);

  async function run(choice: FocusChoice | null) {
    setBusy(true); setErr(null); setText(null); setSignals(null); setFresh(false);
    try {
      // Compute the derived slice locally first (on-device), narrowed to the focus if any.
      const sig = await store.computeSignals(todayLocal(), choice?.input);
      const r = await weaveRemembrance(sig);
      const remembrance = r.remembrance.trim();
      if (!remembrance) {
        setErr({ code: "empty", message: "Save a few more moments and try again — there wasn't enough to weave yet." });
      } else {
        setText(remembrance);
        setSignals(sig);
        setFresh(true);
        setTimeout(() => setFresh(false), 700);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Their story couldn't be woven — please try again." });
    } finally { setBusy(false); }
  }

  if (moments.length === 0) return null;
  return (
    <div className={`recap${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", marginBottom: "1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="eyebrow" style={{ flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ flexShrink: 0 }} /> Their story
        </div>
        <button className="btn btn-primary btn-sm btn-pill" disabled={busy} onClick={() => run(focus)} style={{ flexShrink: 0 }}>
          {busy ? "Weaving…" : text ? (<><RefreshCw size={12} /> Again</>) : "Weave"}
        </button>
      </div>

      {/* Re-weave focus — centre the remembrance on one year / place / milestone (ONE call). */}
      {focusOptions.length > 0 && (
        <div className="seg" role="tablist" aria-label="Story focus" style={{ marginTop: "0.8rem" }}>
          <button role="tab" aria-selected={focus === null} className={`seg-item${focus === null ? " selected" : ""}`} onClick={() => setFocus(null)}>The whole story</button>
          {focusOptions.map((o, i) => (
            <button key={`${o.input.kind}-${o.input.value}-${i}`} role="tab" aria-selected={focus === o}
              className={`seg-item${focus === o ? " selected" : ""}`} onClick={() => setFocus(o)}>{o.label}</button>
          ))}
        </div>
      )}

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
          Gently weaves the moments you saved into a few warm sentences about {petName}{focus ? ` — focused on ${lowerFirst(focus.label)}` : ""}.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Gathering the moments you saved…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "94%" }} />
          <div className="skeleton-line" style={{ width: "68%" }} />
          {/* Reserve the facts column too, so the result lands without a layout shift. */}
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid var(--border-subtle)", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <div className="skeleton-line" style={{ width: "82%", height: 9 }} />
            <div className="skeleton-line" style={{ width: "74%", height: 9 }} />
          </div>
        </div>
      )}

      {err && !busy && <RememberError err={err} onRetry={() => run(focus)} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p className="serif" style={{ fontSize: "var(--fs-lg)", color: "var(--ink-1)", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <FactList signals={signals} />}
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Woven from the moments you saved — comforting words, not counseling or mental-health advice.</span>
            <CopyButton text={copyableRemembrance(text, signals)} />
          </div>
        </div>
      )}
    </div>
  );
}

function RememberError({ err, onRetry }: { err: RememberErr; onRetry: () => void }) {
  // Distress fence — DO NOT retry into the model; surface human support, gently.
  if (err.code === "distress_fence") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12, alignItems: "flex-start" }}>
        <LifeBuoy size={16} style={{ color: "var(--accent-soft-ink)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ lineHeight: 1.6 }}>{err.message}</div>
      </div>
    );
  }
  // Insufficient funds — point to the Oriora account on the web, NO amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to weave their story</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  // BYOK gate — Managed AI blocked for this app; needs a vendor key.
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // Empty result — gentle, no retry button (more moments is the fix, not a retry).
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  // Network / session / unavailable — recoverable, offer a retry.
  const retryable = err.code === "unavailable";
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      {retryable && (
        <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// The receipts: the deterministic facts the remembrance drew from. Purely observational.
function FactList({ signals }: { signals: RemembranceSignals }) {
  const facts = signals.headline_facts.slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "warm" ? "warm" : t === "tender" ? "tender" : "");
  return (
    <div style={{ marginTop: "0.9rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
        <Quote size={11} /> The moments behind this
      </div>
      {facts.map((f: HeadlineFact, i: number) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

// Build the plain-text copy payload: the remembrance plus its grounding facts (no markup).
function copyableRemembrance(text: string, signals: RemembranceSignals | null): string {
  const facts = signals?.headline_facts.slice(0, 5).map((f) => `• ${f.text}`).join("\n") ?? "";
  return facts ? `${text}\n\nThe moments behind this:\n${facts}` : text;
}

/**
 * The memory book, in chapters — ledger row 2b.
 *
 * ⚠️ ROW 2b DOES NOT TRANSFER LITERALLY HERE, and forcing it would have made this app worse. Its
 * test is "does the list rank by the DECISION the user faces?" — but this is a memory book for a
 * cherished or lost pet. There is no decision, nothing is due, and nothing needs doing. Ranking a
 * grieving person's memories by any measure of importance would be both useless and unkind.
 *
 * What DOES carry over is the rule underneath it: use the primitive the object demands. The object
 * is a life, and a life is remembered in chapters — which is exactly what a physical photo album
 * does. A flat reverse-chronological feed of every moment across years makes "her first year"
 * something you scroll for; a year heading makes it something you find.
 *
 * So: same moments, same order, no ranking, no judgement — just the years marked, with a quiet
 * count and the milestone tally for that year. Nothing is hidden or collapsed away.
 */
/**
 * THE RECORD — the living half of the app (2026-08-11 course correction).
 *
 * A memory book is written once and closed; a record is kept. Weight, vet visits and medication
 * are what an owner returns to week after week, and what a vet asks for at the next appointment.
 *
 * Design note: this speaks "Kept Pages", NOT analytics. R3 of the design pass deliberately DELETED
 * the stat grid, sparkline and place bars in favour of prose, so the weight history is a sentence
 * and a row of dated slips — never a chart. Re-adding a graph here would undo the design round.
 */
// ── READY-STATE 2026-09-19 — the one-line door to the first pet + first dated entry ─────────
// ⚠️ This is the LIVING record (PARITY-MATRIX 2026-09-06, and `chat/brain.ts:58` — "do NOT assume
// the pet has died"). The memorial framing in DESIGN.md's a3 block is superseded; nothing here
// reintroduces it, and the optional ending stays where it is, on the pet's own edit sheet.
// Local only: two existing store writes, no AI — the billed story weave is untouched.
const FIRST_PET_KINDS = ["Dog", "Cat", "Rabbit", "Other"] as const;

function FirstRecord({ onStarted }: { onStarted: () => void | Promise<void> }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<string>("Dog");
  const [kg, setKg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function start() {
    const n = name.trim();
    if (!n) { setErr("Their name is the only thing needed — the rest can follow."); return; }
    setBusy(true); setErr(null);
    try {
      const p = await store.createPet({ name: n, kind });
      const weight = Number(kg.replace(/[^0-9.]/g, ""));
      // A weight if one was given — it starts the SERIES that is row 2's difference. Otherwise a
      // dated note, so the record still begins today rather than staying empty.
      if (weight > 0) {
        await store.createCare({ happened_on: todayLocal(), kind: "weight", value_num: weight, title: "" }, p.id);
      } else {
        await store.createCare({ happened_on: todayLocal(), kind: "note", title: "Here, and well", detail: "" }, p.id);
      }
      await onStarted();
    } catch (e) {
      setErr(`Couldn't start the record — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <div className="fp-first">
      <div className="fp-first-rule"><span>Start their record</span></div>
      <div className="fp-first-row">
        <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Their name"
          placeholder="Their name" className="fp-first-name"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
        <input value={kg} onChange={(e) => setKg(e.target.value)} aria-label="Weight today in kg"
          inputMode="decimal" placeholder="kg" className="fp-first-kg"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void start(); } }} />
      </div>
      <div className="fp-first-chips" role="group" aria-label="What they are">
        {FIRST_PET_KINDS.map((k) => (
          <button key={k} type="button" className={`chip${kind === k ? " selected" : ""}`}
            onClick={() => setKind(k)}>{k}</button>
        ))}
      </div>
      {err && <div className="fp-first-err">{err}</div>}
      <button className="btn btn-primary fp-first-go" disabled={busy} onClick={() => void start()}>
        {busy ? "Starting…" : "Start the record"}
      </button>
      <p className="fp-first-foot">
        A weight today starts the series the vet reads. Leave it blank and today is logged as a
        note instead — either way the record begins now.
      </p>
    </div>
  );
}

function TheRecord({ care, pet, petName, onAdd, onEdit, onDelete }: {
  care: Care[];
  pet: Pet | null;
  petName: string;
  onAdd: () => void;
  onEdit: (c: Care) => void;
  onDelete: (c: Care) => void;
}) {
  const trend = useMemo(() => buildWeightTrend(care), [care]);

  // "For the vet" — writes the record out as a plain, printable summary through the OS save sheet
  // (on iPhone that is the Files picker, which IS the permission prompt). Never leaves the device
  // unless the owner puts it somewhere themselves.
  const saveForVet = useCallback(() => {
    const today = todayLocal();
    const text = buildVetSummary(pet, care, today);
    void (async () => {
      const path = await saveFileDialog({ defaultPath: vetSummaryFilename(pet, today) });
      if (path) await writeTextFile(path, text);
    })();
  }, [pet, care]);

  // Honesty guard: a single weigh-in is a fact, not a direction. Only speak of change once
  // there are two readings to compare — buildWeightTrend leaves changeKg null until then.
  const weightProse = (() => {
    if (!trend.latest) return "";
    const kg = `${fmtKg(trend.latest.kg)} kg`;
    if (trend.changeKg === null) return `${kg}, weighed on ${prettyDate(trend.latest.on)}.`;
    const d = trend.changeKg;
    if (Math.abs(d) < 0.05) return `${kg} — the same as the first weight you kept.`;
    return `${kg} — ${fmtKg(Math.abs(d))} kg ${d > 0 ? "more" : "less"} than the first weight you kept.`;
  })();

  return (
    <>
      <div className="sec-head">
        <h2><Stethoscope size={17} style={{ color: "var(--accent)" }} /> Their record</h2>
        <div className="row" style={{ gap: "0.4rem" }}>
          {care.length > 0 && (
            <button className="btn btn-sm" onClick={saveForVet} title="Save a plain summary you can hand to the vet">
              <Share2 size={13} /> For the vet
            </button>
          )}
          <button className="btn btn-sm" onClick={onAdd}><Plus size={14} /> Add</button>
        </div>
      </div>

      {care.length === 0 ? (
        /* READY-STATE 2026-09-19 — the empty record named three of the five dated kinds and, in
           the header above, hid the "For the vet" summary behind `care.length > 0`: the one thing
           this record PRODUCES (PARITY-MATRIX row 9) was switched off for the person who had
           never seen it. All five kinds are now drawn as the dated lines they are, the summary is
           named as the output, and rows 13-14 are stated. ⚠️ Living record, not a memorial — see
           `chat/brain.ts:58` ("do NOT assume the pet has died"); the a3 block in DESIGN.md is the
           superseded memorial framing. */
        <div className="card fade-up fp-record-empty" style={{ padding: "1.25rem 1.3rem", borderRadius: "var(--radius-lg)" }}>
          <p className="recur-line serif" style={{ margin: 0, fontWeight: 500 }}>
            Five things, each with the date you logged it — that is {petName}&rsquo;s record.
          </p>
          <div className="fp-kinds">
            {CARE_KINDS.map((k) => (
              <div key={k.kind} className="fp-kind">
                <span className="fp-kind-dot" aria-hidden />
                <span className="fp-kind-l">{k.label}</span>
                <span className="fp-kind-h">{k.kind === "weight" ? "kept as a series, not one number" : k.hint}</span>
              </div>
            ))}
          </div>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0.85rem 0 0", lineHeight: 1.6 }}>
            A year from now this is the page the vet asks you about — and one tap turns it into a
            plain summary you can hand over at the appointment.
          </p>
          <p className="fp-record-foot">
            It stays on this device and the app is free; a backup goes to your own cloud, not ours.
          </p>
        </div>
      ) : (
        <div className="card fade-up" style={{ padding: "1rem 1.2rem" }}>
          {weightProse && (
            <p className="recur-line serif" style={{ margin: "0 0 0.75rem", fontWeight: 500 }}>
              <Scale size={14} style={{ color: "var(--accent)", verticalAlign: "-2px", marginRight: "0.35rem" }} />
              {weightProse}
            </p>
          )}
          <div style={{ display: "grid", gap: "0.55rem" }}>
            {care.slice(0, 40).map((c) => <CareRow key={c.id} c={c} onEdit={() => onEdit(c)} onDelete={() => onDelete(c)} />)}
          </div>
          {care.length > 40 && (
            <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0.7rem 0 0" }}>
              Showing the 40 most recent of {care.length}. All of them are kept, and all of them back up.
            </p>
          )}
        </div>
      )}
    </>
  );
}

/** Trailing zeros read as false precision on a weight — 4.5 not 4.50, 5 not 5.0. */
function fmtKg(n: number): string {
  return String(Math.round(n * 100) / 100);
}

function CareRow({ c, onEdit, onDelete }: { c: Care; onEdit: () => void; onDelete: () => void }) {
  const meta = CARE_KINDS.find((k) => k.kind === c.kind) ?? CARE_KINDS[3];
  const headline = c.kind === "weight" && typeof c.value_num === "number"
    ? `${fmtKg(c.value_num)} kg`
    : c.title || meta.label;
  // nowrap: .row wraps by default, and a long detail line ("All clear. Teeth looked good. Back in
  // a year.") pushed the edit/delete pair onto its own row, left-aligned under the text.
  return (
    <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
        <div className="row" style={{ gap: "0.45rem", flexWrap: "wrap" }}>
          <span className="chip" style={{ pointerEvents: "none" }}>{meta.label}</span>
          <strong style={{ fontSize: "var(--fs-sm)" }}>{headline}</strong>
        </div>
        <div className="muted" style={{ fontSize: "var(--fs-xs, 0.78rem)", marginTop: "0.2rem" }}>
          {prettyDate(c.happened_on)}
          {c.kind === "weight" && c.title ? ` · ${c.title}` : ""}
        </div>
        {c.detail && (
          <div style={{ fontSize: "var(--fs-sm)", marginTop: "0.25rem", lineHeight: 1.5 }}>{c.detail}</div>
        )}
        {c.due_on && (
          <div className="chip" style={{ marginTop: "0.35rem", pointerEvents: "none", gap: "0.3rem" }}>
            <Bell size={11} /> Next due {prettyDate(c.due_on)}
          </div>
        )}
      </div>
      <div className="row" style={{ gap: "0.3rem", flexShrink: 0 }}>
        <button className="btn btn-sm btn-ghost" onClick={onEdit} aria-label="Edit this record entry"><Pencil size={13} /></button>
        <button className="btn btn-sm btn-ghost" onClick={onDelete} aria-label="Delete this record entry"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

function CareEditor({ editing, petId, onClose, onSaved }: { editing: Care | null; petId: string | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [kind, setKind] = useState<CareKind>(editing?.kind ?? "weight");
  const [happenedOn, setHappenedOn] = useState(editing?.happened_on?.slice(0, 10) ?? todayLocal());
  const [title, setTitle] = useState(editing?.title ?? "");
  const [detail, setDetail] = useState(editing?.detail ?? "");
  const [kg, setKg] = useState(editing?.value_num != null ? String(editing.value_num) : "");
  const [dueOn, setDueOn] = useState(editing?.due_on?.slice(0, 10) ?? "");
  const [showDue, setShowDue] = useState(!!editing?.due_on);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!happenedOn) { setErr("Pick the date."); return; }
    const parsed = Number(kg);
    if (kind === "weight" && (!kg.trim() || !isFinite(parsed) || parsed <= 0)) {
      setErr("Enter the weight in kilograms.");
      return;
    }
    setSaving(true); setErr(null);
    try {
      const fields = {
        happened_on: happenedOn,
        kind,
        title: title.trim(),
        detail: detail.trim(),
        value_num: kind === "weight" ? parsed : null,
        due_on: dueOn,
      };
      if (isEditing) await store.updateCare(editing!.id, fields);
      else await store.createCare(fields, petId);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit record entry" : "Add to the record"} onClose={onClose}>
      <Field label="What is this?">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {CARE_KINDS.map((k) => (
            <button key={k.kind} type="button" className={`chip${kind === k.kind ? " selected" : ""}`}
              onClick={() => setKind(k.kind)} aria-pressed={kind === k.kind}>{k.label}</button>
          ))}
        </div>
      </Field>

      <Field label="Date"><input type="date" value={happenedOn} onChange={(e) => setHappenedOn(e.target.value)} /></Field>

      {kind === "weight" ? (
        <Field label="Weight (kg)">
          <input type="number" inputMode="decimal" step="0.01" min="0" placeholder="4.8"
            value={kg} onChange={(e) => setKg(e.target.value)} autoFocus />
        </Field>
      ) : (
        <Field label={kind === "vet" ? "What was it for?" : kind === "med" ? "What, and how much?" : "Title"}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
            placeholder={kind === "vet" ? "Annual check-up" : kind === "med" ? "Flea treatment, one dose" : "Anything worth keeping"} />
        </Field>
      )}

      <Field label={kind === "weight" ? "Note (optional)" : "Details (optional)"}>
        <textarea rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} style={{ resize: "vertical", minHeight: 64 }}
          placeholder={kind === "vet" ? "What the vet said, and when to come back." : "In your words."} />
      </Field>

      {/* Next-due (1.3.0). Optional and folded away: most rows are a record of what happened, not
          a schedule, and an always-open date picker would imply every entry needs one. */}
      {showDue || dueOn ? (
        <Field label="Remind me when it's next due">
          <input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 5 }}>
            Your phone reminds you that morning. Nothing leaves the device unless you back it up.
            {dueOn && <> <button type="button" className="link" onClick={() => { setDueOn(""); setShowDue(false); }}>Clear</button></>}
          </div>
        </Field>
      ) : (
        <button type="button" className="link" style={{ display: "block", margin: "0 0 0.9rem", fontSize: "var(--fs-xs)" }}
          onClick={() => setShowDue(true)}>
          <Bell size={12} style={{ verticalAlign: "-2px" }} /> Remind me when it&rsquo;s next due
        </button>
      )}

      {err && <div className="notice notice-err" style={{ marginBottom: "0.8rem" }}>{err}</div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave={!!happenedOn}
        saveLabel={isEditing ? "Save" : "Add to the record"} />
    </BottomSheet>
  );
}

function Timeline({ moments, onEdit, onDelete }: { moments: Moment[]; onEdit: (m: Moment) => void; onDelete: (m: Moment) => void }) {
  // `moments` already arrives newest-first (happened_on DESC); grouping preserves that order.
  const chapters = useMemo(() => {
    const out: { year: string; items: Moment[] }[] = [];
    for (const m of moments) {
      const year = (m.happened_on || "").slice(0, 4) || "—";
      const last = out[out.length - 1];
      if (last && last.year === year) last.items.push(m);
      else out.push({ year, items: [m] });
    }
    return out;
  }, [moments]);

  // One chapter isn't a chaptering — don't add furniture that earns nothing.
  if (chapters.length <= 1) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
        {moments.map((m) => (
          <MomentTile key={m.id} m={m} onEdit={() => onEdit(m)} onDelete={() => onDelete(m)} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
      {chapters.map((c) => {
        const milestones = c.items.filter((m) => m.is_milestone).length;
        return (
          <div key={c.year} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
            <div className="chapter-head">
              <span className="serif chapter-year">{c.year}</span>
              <span className="chapter-count tnum">
                {c.items.length} {c.items.length === 1 ? "moment" : "moments"}
                {milestones > 0 ? ` · ${milestones} ${milestones === 1 ? "milestone" : "milestones"}` : ""}
              </span>
              <span className="chapter-rule" aria-hidden />
            </div>
            {c.items.map((m) => (
              <MomentTile key={m.id} m={m} onEdit={() => onEdit(m)} onDelete={() => onDelete(m)} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function MomentTile({ m, onEdit, onDelete }: { m: Moment; onEdit: () => void; onDelete: () => void }) {
  const [zoom, setZoom] = useState(false);
  return (
    <div className={`tile${m.is_milestone ? " milestone" : ""}`} onClick={onEdit} role="button" tabIndex={0}
      aria-label={`Edit moment: ${m.title || prettyDate(m.happened_on)}`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(); } }}>
      {/* An attached photo shows as a thumbnail with tap-to-zoom (never sent to the model). */}
      {m.photo ? (
        <img className="tile-icon" src={convertFileSrc(m.photo)} alt={`Photo from ${m.title || prettyDate(m.happened_on)}`} title="Tap to view"
          onClick={(ev) => { ev.stopPropagation(); setZoom(true); }}
          style={{ objectFit: "cover", cursor: "zoom-in", padding: 0 }} />
      ) : (
        <div className="tile-icon">{m.is_milestone ? <Star size={18} /> : <PawPrint size={18} />}</div>
      )}
      {zoom && m.photo && (
        <PhotoViewer photos={[{ src: convertFileSrc(m.photo), alt: `Photo from ${m.title || prettyDate(m.happened_on)}` }]} onClose={() => setZoom(false)} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 🔴 An untitled moment used to fall back to the DATE as its title — beside the date
            already shown on the right, so every ordinary moment read "Sep 27, 2025 … Sep 27, 2025".
            Most moments in a memory book are untitled, so this was the common case, not the edge
            one. Now the date is stated once and an untitled moment simply leads with her words.
            (Found by opening the store screenshot, not by reading the code.) */}
        <div className="row" style={{ gap: "0.4rem", justifyContent: "space-between", flexWrap: "nowrap" }}>
          {m.title ? (
            <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {m.title}
            </div>
          ) : <span style={{ minWidth: 0 }} />}
          <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", flexShrink: 0 }}>{prettyDate(m.happened_on)}</div>
        </div>
        {m.caption && (
          <div className="muted" style={{
            fontSize: m.title ? "var(--fs-xs)" : "var(--fs-sm)",
            color: m.title ? "var(--ink-2)" : "var(--ink-1)",
            marginTop: m.title ? 2 : 0, lineHeight: 1.5,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>{m.caption}</div>
        )}
        {(m.is_milestone || m.place) && (
          <div className="row" style={{ gap: "0.4rem", marginTop: 6 }}>
            {m.is_milestone && <span className="chip selected" style={{ padding: "0.12rem 0.5rem", fontSize: 10, minHeight: "auto" }}><Star size={10} /> Milestone</span>}
            {m.place && <span className="chip" style={{ padding: "0.12rem 0.5rem", fontSize: 10, minHeight: "auto" }}><MapPin size={10} /> {displayPlace(m.place)}</span>}
          </div>
        )}
      </div>
      <button className="tile-del" title="Delete moment" aria-label="Delete moment"
        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
        <Trash2 size={16} />
      </button>
    </div>
  );
}


// ── Forms ────────────────────────────────────────────────────────────────────
function MomentEditor({ editing, petId, onClose, onSaved }: { editing: Moment | null; petId: string | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [happenedOn, setHappenedOn] = useState(editing?.happened_on?.slice(0, 10) ?? todayLocal());
  const [title, setTitle] = useState(editing?.title ?? "");
  const [caption, setCaption] = useState(editing?.caption ?? "");
  const [place, setPlace] = useState(editing?.place ?? "");
  const [isMilestone, setIsMilestone] = useState(editing?.is_milestone ?? false);
  const [photo, setPhoto] = useState(editing?.photo ?? "");
  const [saving, setSaving] = useState(false);
  const [pickingPhoto, setPickingPhoto] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Hidden <input type=file> for iOS/iPad. The desktop file dialog (openFileDialog) is a
  // NO-OP on iPhone AND iPad — that's the App-Store "Choose photo does nothing" rejection
  // (Apple 2.1(a), the Expense bug). On iOS we open the native photo/camera sheet via this
  // input instead and store the picked bytes locally; the file never leaves the device.
  const photoInputRef = useRef<HTMLInputElement>(null);

  async function pickPhoto() {
    setErr(null);
    // platform() is sync, so the .click() stays inside the tap's user-gesture (required
    // for the iOS sheet to actually present — async work before it would suppress the sheet).
    if (platform() === "ios") { photoInputRef.current?.click(); return; }
    setPickingPhoto(true);
    try {
      // Desktop: pick a path, then copy the file into the app's local photos/ dir so the
      // moment keeps its image even if the original is moved/deleted. Local-only.
      const sel = await openFileDialog({
        multiple: false,
        filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "heic", "heif", "webp", "gif"] }],
      });
      if (typeof sel === "string") setPhoto(await store.savePhotoFromPath(sel));
    } catch (e) {
      setErr(`Could not add the photo — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally { setPickingPhoto(false); }
  }

  // iOS file-input handler: read the picked File (a Blob) bytes and save them into the
  // app's local photos/ dir via Rust, then reference that path. Reset value so re-picking
  // the same image refires the change event.
  async function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null); setPickingPhoto(true);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const ext = (file.name.split(".").pop() || file.type.split("/")[1] || "jpg").toLowerCase().replace("jpeg", "jpg");
      setPhoto(await store.savePhotoBytes(Array.from(buf), ext));
    } catch (e) {
      setErr(`Could not add the photo — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally { setPickingPhoto(false); }
  }

  async function save() {
    if (!happenedOn) { setErr("Pick the date this happened."); return; }
    setSaving(true); setErr(null);
    try {
      const fields = {
        happened_on: happenedOn,
        title: title.trim(),
        caption: caption.trim(),
        place: normalizePlace(place),
        is_milestone: isMilestone,
        photo,
      };
      if (isEditing) await store.updateMoment(editing!.id, fields);
      else await store.createMoment(fields, petId);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit moment" : "Add a moment"} onClose={onClose}>
      <Field label="Date"><input type="date" value={happenedOn} onChange={(e) => setHappenedOn(e.target.value)} /></Field>
      <Field label="Title (optional)"><input placeholder="First snow day, the move, an ordinary morning…" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></Field>
      <Field label="What happened (optional)"><textarea rows={3} placeholder="Tell it in your words — a memory you want to keep." value={caption} onChange={(e) => setCaption(e.target.value)} style={{ resize: "vertical", minHeight: 84 }} /></Field>

      <Field label="Where (optional)">
        <input placeholder="the couch, the park, grandma's…" value={place} onChange={(e) => setPlace(e.target.value)} list="place-suggestions" />
        <datalist id="place-suggestions">{DEFAULT_PLACES.map((p) => <option key={p} value={p} />)}</datalist>
      </Field>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "-0.3rem", marginBottom: "0.9rem" }}>
        {DEFAULT_PLACES.slice(0, 6).map((p) => (
          <button key={p} type="button" className={`chip${normalizePlace(place) === p ? " selected" : ""}`} onClick={() => setPlace(p)}>{displayPlace(p)}</button>
        ))}
      </div>

      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem", gap: "0.5rem" }}>
        <button type="button" className={`chip${isMilestone ? " selected" : ""}`} style={{ padding: "0.5rem 0.75rem" }} onClick={() => setIsMilestone((v) => !v)} aria-pressed={isMilestone}>
          <Star size={13} /> Mark as a milestone
        </button>
        <button type="button" className="btn btn-sm" onClick={pickPhoto} disabled={pickingPhoto}>
          <ImageIcon size={13} /> {pickingPhoto ? "Adding…" : photo ? "Change photo" : "Add a photo"}
        </button>
        {/* iOS/iPad native photo+camera sheet (the desktop dialog is a no-op there). */}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>
      {photo && (
        <div className="row" style={{ fontSize: "var(--fs-xs)", gap: "0.6rem", marginBottom: "0.9rem", padding: "0.5rem 0.7rem", background: "var(--elev-2)", borderRadius: "var(--radius-button)" }}>
          <img src={convertFileSrc(photo)} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 8, flexShrink: 0, background: "var(--elev-3)" }} />
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--ink-2)" }}>{photo.split("/").pop()}</span>
          <button type="button" onClick={() => setPhoto("")} className="tile-del" style={{ minHeight: "auto", padding: "0.2rem" }} aria-label="Remove photo"><X size={13} /></button>
        </div>
      )}

      {err && <div className="notice notice-err" style={{ marginBottom: "0.5rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave saveLabel={isEditing ? "Update" : "Save moment"} />
    </BottomSheet>
  );
}

function PetEditor({ pet, onClose, onSaved, onDelete, onAddAnother, isNew }: {
  pet: Pet | null;
  onClose: () => void;
  onSaved: (created?: Pet) => void;
  onDelete?: () => void;
  onAddAnother?: () => void;
  /** true = this sheet creates an ADDITIONAL companion rather than editing the current one. */
  isNew?: boolean;
}) {
  const [name, setName] = useState(isNew ? "" : pet?.name ?? "");
  const [kind, setKind] = useState(isNew ? "" : pet?.kind ?? "");
  const [birthOn, setBirthOn] = useState(isNew ? "" : pet?.birth_on?.slice(0, 10) ?? "");
  const [farewellOn, setFarewellOn] = useState(isNew ? "" : pet?.farewell_on?.slice(0, 10) ?? "");
  const [showFarewell, setShowFarewell] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true); setErr(null);
    try {
      const fields = { name: name.trim(), kind: kind.trim(), birth_on: birthOn, farewell_on: farewellOn };
      if (isNew) {
        // createPet, NOT upsertPet — upsert would edit the pet already on screen instead of
        // adding a second one, which is the whole point of this sheet.
        const created = await store.createPet(fields);
        onSaved(created);
      } else {
        await store.upsertPet(fields, pet?.id ?? null);
        onSaved();
      }
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isNew ? "Add a companion" : "About your companion"} onClose={onClose}>
      <Field label="Name"><input placeholder="Their name" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      <Field label="Kind (optional)"><input placeholder="dog, cat, rabbit…" value={kind} onChange={(e) => setKind(e.target.value)} /></Field>
      <Field label="Birthday or gotcha day (optional)"><input type="date" value={birthOn} onChange={(e) => setBirthOn(e.target.value)} /></Field>
      {/* Demoted (2026-08-11 course correction): most pets in this app are alive, so the farewell
          is an ending that may never be filled in — available, but never greeting the owner as an
          expectation the moment they open the form. */}
      {showFarewell || !!farewellOn ? (
        <Field label="If they’ve passed, the day you said goodbye (optional)">
          <input type="date" value={farewellOn} onChange={(e) => setFarewellOn(e.target.value)} />
        </Field>
      ) : (
        <button type="button" className="link" style={{ display: "block", margin: "0 0 0.9rem", fontSize: "var(--fs-xs)" }}
          onClick={() => setShowFarewell(true)}>
          They’ve passed — add a farewell date
        </button>
      )}
      {err && <div className="notice notice-err" style={{ marginBottom: "0.5rem" }}><span style={{ fontSize: "var(--fs-xs)" }}>{err}</span></div>}
      <FormActions onCancel={onClose} onSave={save} saving={saving} canSave={!!name.trim()}
        saveLabel={isNew ? "Add companion" : "Save"} />
      {onAddAnother && (
        <button type="button" className="link"
          style={{ display: "block", margin: "0.9rem auto 0", fontSize: "var(--fs-xs)" }}
          onClick={onAddAnother}>
          <Plus size={12} style={{ verticalAlign: "-2px" }} /> Add another companion
        </button>
      )}
      {onDelete && (
        <button type="button" className="link"
          style={{ display: "block", margin: "0.9rem auto 0", fontSize: "var(--fs-xs)", color: "var(--rose)" }}
          onClick={onDelete}>
          Remove this companion
        </button>
      )}
    </BottomSheet>
  );
}

function FormActions({ onCancel, onSave, saving, canSave, saveLabel }: { onCancel: () => void; onSave: () => void; saving: boolean; canSave: boolean; saveLabel: string }) {
  return (
    <div className="row" style={{ gap: "0.6rem", marginTop: "1.1rem", flexWrap: "nowrap" }}>
      <button className="btn btn-pill" style={{ flex: 1 }} onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary btn-pill" style={{ flex: 2 }} onClick={onSave} disabled={saving || !canSave}>{saving ? "Saving…" : saveLabel}</button>
    </div>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: "0.9rem", ...style }}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "var(--ink-2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      {children}
    </div>
  );
}

// Bottom sheet for forms — iOS-native feel; slides up from the bottom edge.
function BottomSheet({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-bottom" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1.1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", letterSpacing: "-0.01em" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

// Centered modal — for the small destructive confirm.
function CenterModal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet sheet-center" role="dialog" aria-modal="true" aria-label={title}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.9rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem" }}>{title}</div>
          <button className="tile-del" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

/** "The park" → "the park" — keep place/label phrases reading naturally mid-sentence. */
function lowerFirst(s: string): string {
  if (!s) return "";
  return s.charAt(0).toLowerCase() + s.slice(1);
}
